import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import api, { feesApi, classesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  IndianRupee, 
  Users, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Plus, 
  Upload, 
  Download,
  Send,
  FileSpreadsheet,
  Loader2,
  Search,
  AlertTriangle,
  Trash2,
  History,
  MessageCircle,
} from 'lucide-react';

const FeesPage = () => {
  const { user } = useAuth();
  const schoolCode = user?.school?.code || '';
  const [fees, setFees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('');
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Payment modal
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [paymentMode, setPaymentMode] = useState('single'); // single | split
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',
    receipt_number: '',
    remarks: '',
    transaction_id: ''
  });
  const [breakdown, setBreakdown] = useState({
    registration: '',
    tuition: '',
    transport: '',
    other_label: '',
    other_amount: '',
  });
  const [splitPayments, setSplitPayments] = useState([
    { amount: '', payment_mode: 'cash', description: 'School Fee', transaction_id: '' },
    { amount: '', payment_mode: 'upi', description: 'School Fee', transaction_id: '' }
  ]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [lastPaymentId, setLastPaymentId] = useState(null);

  // Bulk WhatsApp reminder modal
  const [isBulkWAOpen, setIsBulkWAOpen] = useState(false);
  const [bulkWAList, setBulkWAList] = useState([]);
  const [bulkWALoading, setBulkWALoading] = useState(false);

  // History modal
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyStudent, setHistoryStudent] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  
  // Upload modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // Reminder modal
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);

  // Danger zone: clear all fees
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState('');
  const [clearLoading, setClearLoading] = useState(false);

  useEffect(() => {
    // Load classes once; admin selects one to view its fee status
    (async () => {
      try {
        const res = await classesApi.getAll();
        const list = res.data.classes || [];
        setClasses(list);
        if (list.length > 0 && !classFilter) {
          setClassFilter(list[0].id);
        } else {
          setLoading(false);
        }
      } catch (e) {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (classFilter) {
      loadFees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, classFilter]);

  const loadFees = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (classFilter) {
        params.class_id = classFilter;
      }
      const response = await api.get('/fees', { params });
      setFees(response.data.fees || []);
      setSummary(response.data.summary || {});
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load fee data');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedStudent) {
      toast.error('Please select a student');
      return;
    }

    // Build breakdown payload (only send if something is filled)
    const num = (v) => (v === '' || v === null || v === undefined ? 0 : parseFloat(v) || 0);
    const bdPayload = {
      registration: num(breakdown.registration),
      tuition: num(breakdown.tuition),
      transport: num(breakdown.transport),
      other_label: (breakdown.other_label || '').trim() || null,
      other_amount: num(breakdown.other_amount),
    };
    const bdTotal =
      bdPayload.registration + bdPayload.tuition + bdPayload.transport + bdPayload.other_amount;
    const breakdownArg =
      bdTotal > 0 || bdPayload.other_label ? bdPayload : null;

    setPaymentLoading(true);
    try {
      let response;
      if (paymentMode === 'split') {
        const splits = splitPayments
          .filter((s) => s.amount && parseFloat(s.amount) > 0)
          .map((s) => ({
            amount: parseFloat(s.amount),
            payment_mode: s.payment_mode,
            description: s.description || 'School Fee',
            transaction_id: s.transaction_id || null,
          }));
        if (splits.length === 0) {
          toast.error('Enter at least one split amount');
          setPaymentLoading(false);
          return;
        }
        response = await feesApi.recordSplitPayment({
          student_id: selectedStudent.student_id,
          payment_date: paymentData.payment_date,
          payments: splits,
          remarks: paymentData.remarks || null,
          breakdown: breakdownArg,
        });
      } else {
        if (!paymentData.amount) {
          toast.error('Enter amount');
          setPaymentLoading(false);
          return;
        }
        response = await feesApi.recordPayment({
          student_id: selectedStudent.student_id,
          amount: parseFloat(paymentData.amount),
          payment_date: paymentData.payment_date,
          payment_mode: paymentData.payment_mode,
          receipt_number: paymentData.receipt_number || null,
          remarks: paymentData.remarks || null,
          transaction_id: paymentData.transaction_id || null,
          breakdown: breakdownArg,
        });
      }

      const paymentId = response?.data?.id;
      const receiptNo = response?.data?.receipt_number;
      setLastPaymentId(paymentId);
      toast.success(`Payment recorded (Receipt: ${receiptNo || '-'} )`);

      // Auto-download receipt
      if (paymentId) {
        window.open(feesApi.downloadReceipt(paymentId), '_blank', 'noopener,noreferrer');
      }

      setIsPaymentModalOpen(false);
      setSelectedStudent(null);
      setPaymentMode('single');
      setPaymentData({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_mode: 'cash',
        receipt_number: '',
        remarks: '',
        transaction_id: ''
      });
      setBreakdown({ registration: '', tuition: '', transport: '', other_label: '', other_amount: '' });
      setSplitPayments([
        { amount: '', payment_mode: 'cash', description: 'School Fee', transaction_id: '' },
        { amount: '', payment_mode: 'upi', description: 'School Fee', transaction_id: '' }
      ]);
      loadFees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const openPaymentDialog = (fee) => {
    setSelectedStudent(fee);
    setPaymentMode('single');
    // Pre-fill breakdown from fee structure defaults
    const bd = fee.breakdown_default || {};
    setBreakdown({
      registration: bd.registration ? String(bd.registration) : '',
      tuition: bd.tuition ? String(bd.tuition) : '',
      transport: bd.transport ? String(bd.transport) : '',
      other_label: bd.other_label || '',
      other_amount: bd.other_amount ? String(bd.other_amount) : '',
    });
    setIsPaymentModalOpen(true);
  };

  const breakdownTotal = (() => {
    const num = (v) => (v === '' || v === null || v === undefined ? 0 : parseFloat(v) || 0);
    return (
      num(breakdown.registration) +
      num(breakdown.tuition) +
      num(breakdown.transport) +
      num(breakdown.other_amount)
    );
  })();

  const applyBreakdownToAmount = () => {
    setPaymentData((p) => ({ ...p, amount: String(breakdownTotal || '') }));
  };

  const sendWhatsAppReminder = async (fee) => {
    try {
      const res = await feesApi.whatsappReminder(fee.student_id);
      const { wa_url, has_phone } = res.data || {};
      if (!wa_url) {
        toast.error('Unable to generate reminder link');
        return;
      }
      if (!has_phone) {
        toast.warning('No parent phone on file — opening blank WhatsApp');
      }
      window.open(wa_url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const openBulkWhatsApp = async () => {
    setBulkWALoading(true);
    setIsBulkWAOpen(true);
    try {
      const params = {};
      if (classFilter) params.class_id = classFilter;
      const res = await feesApi.whatsappRemindersBulk(params);
      setBulkWAList(res.data?.reminders || []);
    } catch (e) {
      toast.error('Failed to load pending list');
      setIsBulkWAOpen(false);
    } finally {
      setBulkWALoading(false);
    }
  };

  const openAllBulkWA = () => {
    const withPhone = bulkWAList.filter((r) => r.has_phone);
    if (withPhone.length === 0) {
      toast.error('No students with parent phone numbers');
      return;
    }
    // Open each link with a small stagger so browser doesn't block
    withPhone.forEach((r, i) => {
      setTimeout(() => {
        window.open(r.wa_url, '_blank', 'noopener,noreferrer');
      }, i * 400);
    });
    toast.success(`Opening ${withPhone.length} WhatsApp chats…`);
  };

  const openHistory = async (fee) => {
    setHistoryStudent(fee);
    setIsHistoryOpen(true);
    try {
      const res = await feesApi.getStudent(fee.student_id);
      setHistoryData(res.data);
    } catch (e) {
      toast.error('Failed to load payment history');
    }
  };

  const addSplit = () =>
    setSplitPayments([
      ...splitPayments,
      { amount: '', payment_mode: 'cash', description: 'School Fee', transaction_id: '' },
    ]);
  const removeSplit = (idx) =>
    setSplitPayments(splitPayments.filter((_, i) => i !== idx));
  const updateSplit = (idx, key, val) => {
    const next = [...splitPayments];
    next[idx] = { ...next[idx], [key]: val };
    setSplitPayments(next);
  };
  const splitTotal = splitPayments.reduce(
    (sum, s) => sum + (parseFloat(s.amount) || 0),
    0
  );

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const response = await api.post('/fees/payment/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${response.data.success_count} payments imported`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      setIsUploadModalOpen(false);
      setUploadFile(null);
      loadFees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setReminderLoading(true);
    try {
      const response = await api.post('/fees/reminder', {});
      toast.success(`Reminder logged for ${response.data.students_notified} students`);
      toast.info('Note: Actual SMS/Email will be sent when keys are configured');
      setIsReminderModalOpen(false);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setReminderLoading(false);
    }
  };

  const handleClearAllFees = async () => {
    if (clearConfirmText.trim().toUpperCase() !== (schoolCode || '').toUpperCase()) {
      toast.error(`Please type "${schoolCode}" exactly to confirm`);
      return;
    }
    setClearLoading(true);
    try {
      const res = await feesApi.clearAllForSchool(schoolCode);
      toast.success(
        `Cleared ${res.data.payments_deleted} payments, ${res.data.counters_reset} counters for ${res.data.school}`
      );
      setIsClearModalOpen(false);
      setClearConfirmText('');
      loadFees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear fees');
    } finally {
      setClearLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Paid</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Partial</Badge>;
      case 'unpaid':
        return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Unpaid</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const filteredFees = fees.filter(f => 
    !search || 
    f.student_name?.toLowerCase().includes(search.toLowerCase()) ||
    f.roll_number?.toLowerCase().includes(search.toLowerCase()) ||
    f.phone?.toLowerCase().includes(search.toLowerCase()) ||
    f.class_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-6" data-testid="fees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Fee Management</h1>
          <p className="text-muted-foreground mt-1">Track and manage student fees</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/fees/export?${new URLSearchParams({
              ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
              ...(classFilter ? { class_id: classFilter } : {}),
            }).toString()}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="export-fees-link"
          >
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </a>
          <Button variant="outline" onClick={() => setIsUploadModalOpen(true)} data-testid="upload-payments-button">
            <Upload className="h-4 w-4 mr-2" />
            Import Payments
          </Button>
          <Button
            variant="outline"
            className="border-green-600/40 text-green-600 hover:bg-green-600/10"
            onClick={openBulkWhatsApp}
            data-testid="bulk-whatsapp-reminders-button"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            WhatsApp Unpaid
          </Button>
          <Button variant="outline" onClick={() => setIsReminderModalOpen(true)} data-testid="send-reminders-button">
            <Send className="h-4 w-4 mr-2" />
            Send Reminders
          </Button>
          <Button
            variant="outline"
            className="border-red-500/40 text-red-600 hover:bg-red-500/10"
            onClick={() => setIsClearModalOpen(true)}
            data-testid="clear-all-fees-button"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All Fees
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Students</p>
                  <p className="text-2xl font-bold">{summary.total_students || 0}</p>
                </div>
                <Users className="h-8 w-8 text-primary/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Collected</p>
                  <p className="text-2xl font-bold text-green-500">₹{(summary.total_collected || 0).toLocaleString()}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold text-red-500">₹{(summary.total_pending || 0).toLocaleString()}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Paid</p>
                  <p className="text-2xl font-bold text-green-500">{summary.paid_count || 0}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Unpaid</p>
                  <p className="text-2xl font-bold text-red-500">{summary.unpaid_count || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-64">
              <Select value={classFilter} onValueChange={(v) => { setPage(1); setClassFilter(v); }}>
                <SelectTrigger data-testid="filter-class-select">
                  <SelectValue placeholder="Select a class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.section ? ` ${c.section}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, roll, class, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-fees-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-status-select">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Fee Table */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-primary" />
            Student Fee Status
            {classFilter && classes.find((c) => c.id === classFilter) && (
              <span className="text-sm text-muted-foreground font-normal">
                — {classes.find((c) => c.id === classFilter)?.name}
                {classes.find((c) => c.id === classFilter)?.section
                  ? ` ${classes.find((c) => c.id === classFilter)?.section}`
                  : ''}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner h-8 w-8 text-primary" />
            </div>
          ) : filteredFees.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Roll No.</TableHead>
                      <TableHead>Student Name</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Total Fee</TableHead>
                      <TableHead>Paid</TableHead>
                      <TableHead>Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFees.map((fee) => (
                      <TableRow key={fee.student_id} className="table-row-hover">
                        <TableCell>{fee.roll_number}</TableCell>
                        <TableCell className="font-medium">{fee.student_name}</TableCell>
                        <TableCell>{fee.class_name || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{fee.phone || '-'}</TableCell>
                        <TableCell>₹{fee.total_fee?.toLocaleString()}</TableCell>
                        <TableCell className="text-green-600">₹{fee.paid_amount?.toLocaleString()}</TableCell>
                        <TableCell className={fee.balance > 0 ? 'text-red-600' : 'text-green-600'}>
                          ₹{fee.balance?.toLocaleString()}
                        </TableCell>
                        <TableCell>{getStatusBadge(fee.status)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="mr-2 border-green-600/40 text-green-600 hover:bg-green-600/10"
                            onClick={() => sendWhatsAppReminder(fee)}
                            disabled={fee.status === 'paid'}
                            title={fee.parent_phone ? `Reminder to ${fee.parent_phone}` : 'No parent phone on file'}
                            data-testid={`wa-reminder-${fee.student_id}`}
                          >
                            <MessageCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="mr-2"
                            onClick={() => openHistory(fee)}
                            data-testid={`history-payment-${fee.student_id}`}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openPaymentDialog(fee)}
                            disabled={fee.status === 'paid'}
                            data-testid={`record-payment-${fee.student_id}`}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Payment
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages} ({total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <IndianRupee className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No fee records found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Record Payment Modal */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium">{selectedStudent.student_name}</p>
                <p className="text-sm text-muted-foreground">Roll: {selectedStudent.roll_number}</p>
                <p className="text-sm">Balance: <span className="text-red-500 font-medium">₹{selectedStudent.balance?.toLocaleString()}</span></p>
                {selectedStudent.parent_phone ? (
                  <p className="text-xs text-muted-foreground">Parent: {selectedStudent.parent_phone}</p>
                ) : null}
              </div>

              {/* Fee Breakdown (pre-filled from fee structure; editable) */}
              <div className="space-y-2 border rounded-md p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Fee Breakdown (optional)</Label>
                  <button
                    type="button"
                    onClick={applyBreakdownToAmount}
                    className="text-xs text-primary hover:underline"
                    data-testid="apply-breakdown-to-amount"
                  >
                    Use total as amount
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">i. Registration / Admission</Label>
                    <Input
                      type="number"
                      value={breakdown.registration}
                      onChange={(e) => setBreakdown({ ...breakdown, registration: e.target.value })}
                      placeholder="0"
                      data-testid="breakdown-registration"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">ii. Tuition Fees</Label>
                    <Input
                      type="number"
                      value={breakdown.tuition}
                      onChange={(e) => setBreakdown({ ...breakdown, tuition: e.target.value })}
                      placeholder="0"
                      data-testid="breakdown-tuition"
                    />
                  </div>
                  {selectedStudent.uses_transport ? (
                    <div className="space-y-1">
                      <Label className="text-xs">iii. Transport Charges</Label>
                      <Input
                        type="number"
                        value={breakdown.transport}
                        onChange={(e) => setBreakdown({ ...breakdown, transport: e.target.value })}
                        placeholder="0"
                        data-testid="breakdown-transport"
                      />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">iii. Transport</Label>
                      <p className="text-xs text-muted-foreground pt-2 italic">
                        Not a bus user
                      </p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">iv. Other (label)</Label>
                    <Input
                      value={breakdown.other_label}
                      onChange={(e) => setBreakdown({ ...breakdown, other_label: e.target.value })}
                      placeholder="e.g., Books"
                      data-testid="breakdown-other-label"
                    />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">iv. Other (amount)</Label>
                    <Input
                      type="number"
                      value={breakdown.other_amount}
                      onChange={(e) => setBreakdown({ ...breakdown, other_amount: e.target.value })}
                      placeholder="0"
                      data-testid="breakdown-other-amount"
                    />
                  </div>
                </div>
                <p className="text-xs text-right text-muted-foreground">
                  Breakdown total:{' '}
                  <span className="font-semibold text-primary">
                    ₹{breakdownTotal.toLocaleString()}
                  </span>
                </p>
              </div>

              <Tabs value={paymentMode} onValueChange={setPaymentMode} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="single" data-testid="single-payment-tab">Single Mode</TabsTrigger>
                  <TabsTrigger value="split" data-testid="split-payment-tab">Split (Cash + UPI)</TabsTrigger>
                </TabsList>

                <TabsContent value="single" className="space-y-3 pt-3">
                  <div className="space-y-2">
                    <Label>Amount *</Label>
                    <Input
                      type="number"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                      placeholder="Enter amount"
                      max={selectedStudent.balance}
                      data-testid="payment-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Mode *</Label>
                    <Select
                      value={paymentData.payment_mode}
                      onValueChange={(val) => setPaymentData({ ...paymentData, payment_mode: val })}
                    >
                      <SelectTrigger data-testid="payment-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="online">Online Transfer</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Transaction ID / UTR / Cheque No.</Label>
                    <Input
                      value={paymentData.transaction_id}
                      onChange={(e) =>
                        setPaymentData({ ...paymentData, transaction_id: e.target.value })
                      }
                      placeholder="optional — leave blank for cash"
                      data-testid="payment-transaction-id-input"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="split" className="space-y-3 pt-3">
                  <p className="text-xs text-muted-foreground">
                    Record one transaction with multiple payment modes (e.g., ₹10,000 Cash + ₹5,000 UPI).
                    Balance due will auto-update.
                  </p>
                  {splitPayments.map((s, idx) => (
                    <div key={idx} className="space-y-2 p-3 border rounded-md" data-testid={`split-row-${idx}`}>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-5">
                          <Label className="text-xs">Amount</Label>
                          <Input
                            type="number"
                            value={s.amount}
                            onChange={(e) => updateSplit(idx, 'amount', e.target.value)}
                            placeholder="0"
                            data-testid={`split-amount-${idx}`}
                          />
                        </div>
                        <div className="col-span-5">
                          <Label className="text-xs">Mode</Label>
                          <Select
                            value={s.payment_mode}
                            onValueChange={(v) => updateSplit(idx, 'payment_mode', v)}
                          >
                            <SelectTrigger data-testid={`split-mode-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cash">Cash</SelectItem>
                              <SelectItem value="upi">UPI</SelectItem>
                              <SelectItem value="cheque">Cheque</SelectItem>
                              <SelectItem value="online">Online</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeSplit(idx)}
                            disabled={splitPayments.length <= 1}
                            data-testid={`remove-split-${idx}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Transaction ID / UTR / Cheque No.</Label>
                        <Input
                          value={s.transaction_id || ''}
                          onChange={(e) => updateSplit(idx, 'transaction_id', e.target.value)}
                          placeholder={s.payment_mode === 'cash' ? 'optional for cash' : 'enter UTR / cheque no.'}
                          data-testid={`split-txn-${idx}`}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSplit}
                      data-testid="add-split-button"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add mode
                    </Button>
                    <p className="text-sm font-medium">
                      Total: <span className="text-primary">₹{splitTotal.toLocaleString()}</span>
                    </p>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                  required
                  data-testid="payment-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Input
                  value={paymentData.remarks}
                  onChange={(e) => setPaymentData({ ...paymentData, remarks: e.target.value })}
                  placeholder="Any additional notes"
                  data-testid="payment-remarks-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsPaymentModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={paymentLoading} data-testid="submit-payment-button">
                  {paymentLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Record & Download Receipt
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment History Modal */}
      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Payment History — {historyStudent?.student_name}
            </DialogTitle>
          </DialogHeader>
          {!historyData ? (
            <div className="py-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="p-3 bg-muted/50 rounded">
                  <p className="text-muted-foreground">Total Fee</p>
                  <p className="font-semibold">₹{historyData.total_fee?.toLocaleString()}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded">
                  <p className="text-muted-foreground">Paid</p>
                  <p className="font-semibold text-green-600">₹{historyData.paid_amount?.toLocaleString()}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded">
                  <p className="text-muted-foreground">Balance</p>
                  <p className="font-semibold text-red-600">₹{historyData.balance?.toLocaleString()}</p>
                </div>
              </div>
              <div className="overflow-x-auto max-h-96">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Receipt</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Mode(s)</TableHead>
                      <TableHead className="text-right">Download</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(historyData.payments || []).map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.payment_date}</TableCell>
                        <TableCell className="font-mono text-xs">{p.receipt_number}</TableCell>
                        <TableCell>₹{(p.total_amount ?? p.amount ?? 0).toLocaleString()}</TableCell>
                        <TableCell>
                          {(p.payments || []).length > 0
                            ? p.payments.map((x) => `${x.payment_mode}`).join(' + ')
                            : p.payment_mode}
                        </TableCell>
                        <TableCell className="text-right">
                          <a
                            href={feesApi.downloadReceipt(p.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`download-receipt-${p.id}`}
                          >
                            <Button size="sm" variant="outline">
                              <Download className="h-4 w-4" />
                            </Button>
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Fee Payments</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload an Excel or CSV file with columns: student_id (or roll_number), amount, payment_date, payment_mode
            </p>
            
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="mt-2"
                data-testid="upload-file-input"
              />
              {uploadFile && (
                <p className="text-sm text-green-500 mt-2">{uploadFile.name}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsUploadModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleBulkUpload} disabled={!uploadFile || uploadLoading} data-testid="upload-submit-button">
                {uploadLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Reminders Modal */}
      <Dialog open={isReminderModalOpen} onOpenChange={setIsReminderModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Fee Reminders</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                <AlertCircle className="h-4 w-4 inline mr-2" />
                SMS/Email notifications will be sent when Twilio/SendGrid keys are configured.
                For now, reminders will be logged for record.
              </p>
            </div>
            
            <p className="text-sm text-muted-foreground">
              This will send a fee payment reminder to all students with pending fees.
              Currently {summary?.unpaid_count || 0} unpaid and {summary?.partial_count || 0} partially paid students.
            </p>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsReminderModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendReminders} disabled={reminderLoading} data-testid="confirm-reminder-button">
                {reminderLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send Reminders
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clear All Fees Modal (Danger Zone) */}
      <Dialog open={isClearModalOpen} onOpenChange={setIsClearModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Clear ALL fee data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This will <b className="text-red-600">permanently delete</b> every
              fee payment, receipt record and counter for{' '}
              <b>{user?.school?.name || 'your school'}</b>.
              Students and teachers will <b>not</b> be affected. This cannot be
              undone.
            </p>
            <div className="p-3 rounded bg-red-500/5 border border-red-500/20 text-sm">
              To confirm, type your school code{' '}
              <code className="font-mono font-bold">{schoolCode || 'CODE'}</code>{' '}
              below.
            </div>
            <Input
              value={clearConfirmText}
              onChange={(e) => setClearConfirmText(e.target.value)}
              placeholder={schoolCode || 'Type school code to confirm'}
              data-testid="clear-confirm-input"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsClearModalOpen(false);
                  setClearConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleClearAllFees}
                disabled={
                  clearLoading ||
                  clearConfirmText.trim().toUpperCase() !== (schoolCode || '').toUpperCase()
                }
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-clear-fees-button"
              >
                {clearLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Permanently delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Bulk WhatsApp Reminders Modal */}
      <Dialog open={isBulkWAOpen} onOpenChange={setIsBulkWAOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-600" />
              Send WhatsApp reminders to unpaid parents
            </DialogTitle>
          </DialogHeader>
          {bulkWALoading ? (
            <div className="py-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {bulkWAList.length} student{bulkWAList.length === 1 ? '' : 's'} with pending fees
                {classFilter ? ' in selected class' : ' across all classes'}. Clicking{' '}
                <b>Open all</b> will launch a WhatsApp chat for each parent with a
                pre-filled message — confirm and hit send in each tab.
              </p>
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-700 dark:text-yellow-400">
                Tip: if your browser blocks multiple tabs, allow pop-ups for this
                site once and try again.
              </div>
              <div className="overflow-x-auto max-h-80">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Balance</TableHead>
                      <TableHead>Parent Phone</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkWAList.map((r) => (
                      <TableRow key={r.student_id}>
                        <TableCell className="font-medium">{r.student_name}</TableCell>
                        <TableCell>{r.class_name || '-'}</TableCell>
                        <TableCell className="text-red-600">
                          ₹{r.balance?.toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {r.has_phone ? r.phone : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <a
                            href={r.wa_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`bulk-wa-open-${r.student_id}`}
                          >
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-green-600/40 text-green-600 hover:bg-green-600/10"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </Button>
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setIsBulkWAOpen(false)}>
                  Close
                </Button>
                <Button
                  onClick={openAllBulkWA}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="bulk-wa-open-all-button"
                  disabled={bulkWAList.length === 0}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Open all ({bulkWAList.filter((r) => r.has_phone).length})
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeesPage;
